import { CheckCircleIcon } from "@phosphor-icons/react/dist/csr/CheckCircle";
import { WarningIcon } from "@phosphor-icons/react/dist/csr/Warning";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";
import {
  STATEMENT_IDENTITY,
  useGetAiScriptStatement,
} from "@/payroll/ai-script/query";
import { useGetPackApproval } from "@/payroll/pack-approval/query";
import { usePaypacksAccess } from "@/payroll/pay-breakdown/payroll-emails-access";

interface PayApprovalHeaderStatusProps {
  businessId: string | null;
  /** The requested run. The sent run is resolved from the statement, as elsewhere. */
  runId: string;
  isLocked: boolean;
}

/**
 * Whether this period still needs the technician's sign-off, in the one strip of the
 * screen that never scrolls away.
 *
 * The control itself is at the foot of the pack, which on a real statement is several
 * screens down — so a technician who opens their pay has no way of knowing anything
 * is being asked of them until they happen to reach the bottom. This says it where
 * they are already looking, and the pack is what explains it.
 *
 * Colour on an icon and a word, no chip: it sits on the "Pay Period" label's line,
 * where a filled badge outweighed the label it sits beside and pushed the period
 * selector out of the row. Keeping it to the label's height leaves the selector the
 * full width underneath.
 *
 * Both queries here are the ones the statement view already runs, with the same keys,
 * so this reads them out of the cache rather than asking again.
 */
export const PayApprovalHeaderStatus = ({
  businessId,
  runId,
  isLocked,
}: PayApprovalHeaderStatusProps) => {
  const { enabled: approvalsEnabled, isLoading: productsLoading } =
    usePaypacksAccess();
  const { data: statement } = useGetAiScriptStatement(businessId, runId, {
    identity: STATEMENT_IDENTITY.viewer,
    enabled: true,
  });
  // Pay that is still withheld has nothing to sign off, and the endpoint refuses it —
  // the same wait the statement itself is showing.
  const released = !!statement && statement.released !== false;
  // Nor does a period they earned nothing in. This strip lives above the statement
  // rather than inside it, so without this check a technician whose pack is empty was
  // asked to review something, scrolled to "No pay this period", and found no control
  // to act on — the view returns before the panel is ever rendered.
  const hasPay = (statement?.statement?.blocks?.length ?? 0) > 0;
  const statementRunId = statement?.statement_run_id ?? runId;
  const { data } = useGetPackApproval(
    businessId,
    statementRunId,
    !productsLoading && approvalsEnabled && released && hasPay,
    { followsStatement: statement },
  );

  // Nothing to say until the answer is known. A "needs review" flag that appears a
  // second after the page settles, on somebody's pay, reads as an alert.
  //
  // The gates are re-checked here rather than left to the query's `enabled`: turning a
  // query off does not discard what it already cached, so a selection turned off — or pay
  // that has gone back to withheld — would otherwise keep rendering a badge off a
  // stale answer.
  if (productsLoading || !approvalsEnabled || !released || !hasPay || !data)
    return null;

  const approval = data.approval;
  // `!== false`, so an approval the API could not compare is still not something to
  // ask about again: nothing can show that this technician's pay has changed
  // (ENG-2511).
  const signedOff = !!approval && approval.current !== false;
  // It is not a confirmed tick either. A green "Approved" beside a figure says this is
  // the figure they signed off, and a `null` comparison cannot say that — swapping one
  // unsupported claim for its opposite. So it gets its own neutral face, which asks
  // nothing of them and asserts nothing about the number.
  const unverified = !!approval && approval.current === null;

  // A period nobody can act on any more: report a standing approval, but never ask
  // for one that cannot be given.
  if (isLocked && !signedOff) return null;

  if (signedOff) {
    return (
      <Stack direction="row" align="center" gap={1} className="shrink-0">
        <CheckCircleIcon
          className={cn("size-4 shrink-0", {
            "text-subtle": unverified,
            "text-success": !unverified,
          })}
          aria-hidden
        />
        <Text
          variant="caption"
          className={cn({
            "text-subtle": unverified,
            "text-success": !unverified,
          })}
        >
          {unverified ? "Approval on file" : "Approved"}
        </Text>
      </Stack>
    );
  }

  // Not approved, or approved before a rerun moved the figures — both are still
  // waiting on them, and the pack below says which.
  return (
    <Stack direction="row" align="center" gap={1} className="shrink-0">
      <WarningIcon className="size-4 shrink-0 text-warning" aria-hidden />
      <Text variant="caption" className="text-warning">
        {approval ? "Needs review again" : "Needs your review"}
      </Text>
    </Stack>
  );
};
